<template>
  <div>
    <div class="table-responsive" v-if="items && items.length">
      <table class="table table-lg">
        <thead>
          <tr>
            <th
              scope="col"
              v-for="field in visibleFields"
              :key="field.code"
            >
              {{ field.name }}
            </th>
            <th scope="col" v-if="hasRowActions">Acciones</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in items" :key="item.id ?? index">
            <td
              v-for="field in visibleFields"
              :key="field.code"
              :class="field.type"
            >
                <template v-if="field.type === 'currency'">
                  <span
                    class="currency-value"
                    :class="{ 'fx-amount': isFxConverted(item, field.code), 'insoluto-value': isInsolutoField(field.code) }"
                    :data-bs-content="getFxPopoverContent(item, field.code)"
                    :data-bs-toggle="isFxConverted(item, field.code) ? 'popover' : null"
                    data-bs-trigger="click"
                    data-bs-placement="top"
                    tabindex="0"
                  >
                    {{ formatCell(item, field) }}
                  </span>
                </template>
                <template v-else>
                  <template v-if="isRelatedDocumentsField(field.code)">
                    <div v-if="getRelatedDocuments(item, field.code).length">
                      <div
                        v-for="(documentUuid, docIndex) in getRelatedDocuments(item, field.code)"
                        :key="`${item.id ?? index}-doc-${docIndex}`"
                        class="related-doc-line"
                      >
                        <button
                          type="button"
                          class="related-doc-badge related-doc-btn"
                          @click="openCfdiPreview(documentUuid, { sourceItem: item, fieldCode: field.code })"
                        >
                          {{ formatUuid(documentUuid) }}
                        </button>
                      </div>
                    </div>
                    <span v-else>-</span>
                  </template>
                  <template v-else-if="isUuidField(field.code) && formatCell(item, field)">
                    <button
                      type="button"
                      class="uuid-link-btn"
                      :title="'Ver detalle: ' + formatCell(item, field)"
                      @click="openCfdiPreview(formatCell(item, field))"
                    >
                      {{ formatCell(item, field) }}
                    </button>
                  </template>
                  <template v-else>
                    {{ formatCell(item, field) }}
                  </template>
                </template>
            </td>

            <td v-if="hasRowActions" class="actions-cell">
              <button
                v-for="action in rowActions"
                :key="`${action.key}-${item.id ?? index}`"
                type="button"
                class="btn btn-xs action-btn"
                :class="action.className || 'btn-outline-primary'"
                :disabled="Boolean(action.disabled)"
                :data-bs-content="action.popoverText || action.label || ''"
                :data-bs-toggle="(action.popoverText || action.label) ? 'popover' : null"
                :data-bs-trigger="action.popoverTrigger || 'hover focus'"
                :data-bs-placement="action.popoverPlacement || 'top'"
                @click="emitRowAction(action, item)"
              >
                <i :class="action.iconClass || 'fa fa-check-circle'" aria-hidden="true"></i>
                <span class="action-label">{{ action.buttonText || action.label || 'Pagar' }}</span>
                <span class="sr-only">{{ action.label || 'Accion' }}</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else style="width: 100%; margin: 50px 0 0 0; text-align: center;">
      <h5>{{ emptyText }}</h5>
    </div>



    <CfdiPreviewModal
      :visible="cfdiPreviewModal.visible"
      :uuid="cfdiPreviewModal.uuid"
      :rfc="rfc"
      :sourceItem="cfdiPreviewModal.sourceItem"
      :fieldCode="cfdiPreviewModal.fieldCode"
      @close="closeCfdiPreview"
    />
  </div>
</template>

<script>
import { Popover } from "bootstrap";
import CfdiPreviewModal from "@/components/cfdis/CfdiPreviewModal.vue";

export default {
  name: "CfdiTable",
  UUID_FIELD_CODES: ["uuid", "uuid_cfdi", "folio_fiscal"],
  components: { CfdiPreviewModal },
  props: {
    items: { type: Array, default: () => [] },          // cfdis
    fields: { type: Array, default: () => [] },         // available_fields
    emptyText: {
      type: String,
      default: "No se encontraron CFDIs en el periodo seleccionado.",
    },
    rowActions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["row-action"],
  data() {
    return {
      rfc: "",
      cfdiPreviewModal: {
        visible: false,
        uuid: '',
        sourceItem: null,
        fieldCode: null,
      },
    };
  },
  mounted() {
    this.rfc = localStorage.getItem("rfc") || "";
    this.initPopovers();
  },
  updated() {
    this.initPopovers();
  },
  beforeUnmount() {
    this.disposePopovers();
  },
  beforeDestroy() {
    this.disposePopovers();
  },
  computed: {
    visibleFields() {
      return (this.fields || []).filter((f) => !!f.visible);
    },
    hasRowActions() {
      return Array.isArray(this.rowActions) && this.rowActions.length > 0;
    },
  },
  methods: {
    emitRowAction(action, item) {
      this.$emit("row-action", { action, item });
    },
    initPopovers() {
      this.$nextTick(() => {
        const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
        elements.forEach((element) => {
          let instance = Popover.getInstance(element);
          if (!instance) {
            instance = new Popover(element, {
              trigger: element.getAttribute("data-bs-trigger") || "click",
              html: false,
              container: "body",
              placement: element.getAttribute("data-bs-placement") || "top",
              customClass: "fx-popover",
            });
          }

          if (!element.dataset.fxPopoverBound) {
            element.addEventListener("show.bs.popover", () => {
              this.closeOtherPopovers(element);
            });
            element.dataset.fxPopoverBound = "1";
          }
        });
      });
    },
    closeOtherPopovers(currentElement) {
      if (!this.$el) {
        return;
      }

      const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
      elements.forEach((element) => {
        if (element === currentElement) {
          return;
        }

        const instance = Popover.getInstance(element);
        if (instance) {
          instance.hide();
        }
      });
    },
    disposePopovers() {
      if (!this.$el) {
        return;
      }

      const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
      elements.forEach((element) => {
        const instance = Popover.getInstance(element);
        if (instance) {
          instance.dispose();
        }
      });
    },
    formatCurrency(value) {
      const normalized = Number(String(value ?? 0).replace(/,/g, ""));
      const formatted = new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isNaN(normalized) ? 0 : normalized);

      return formatted
        .replace(/^\$/, "$ ")
        .replace(/^-\$/, "-$ ");
    },
      normalizeCurrency(value) {
        const normalized = String(value || 'MXN').trim().toUpperCase();
        return normalized || 'MXN';
      },
      isFxConverted(item, fieldCode) {
        const currency = this.normalizeCurrency(item?.moneda_original || item?.moneda);
        const hasOriginal = item?.[`${fieldCode}_original`] !== undefined && item?.[`${fieldCode}_original`] !== null;
        return Boolean(item?.fx_converted && hasOriginal && currency !== 'MXN');
      },
      formatOriginalCurrency(amount, currencyCode) {
        const normalizedAmount = Number(amount || 0);
        const normalizedCurrency = this.normalizeCurrency(currencyCode);

        try {
          return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(normalizedAmount);
        } catch (error) {
          return `${normalizedCurrency} ${normalizedAmount.toFixed(2)}`;
        }
      },
      getFxPopoverContent(item, fieldCode) {
        if (!this.isFxConverted(item, fieldCode)) {
          return '';
        }

        const currency = this.normalizeCurrency(item?.moneda_original || item?.moneda);
        const originalValue = Number(item?.[`${fieldCode}_original`] || 0);
        const exchangeRate = Number(item?.tipo_cambio_aplicado || item?.tipo_cambio || 1);

        return `Convertido desde ${this.formatOriginalCurrency(originalValue, currency)} con TC ${exchangeRate.toFixed(4)}.`;
      },
    isRelatedDocumentsField(fieldCode) {
      const code = String(fieldCode || "").toLowerCase();
      return code === "documentos_relacionados" || code === "cfdis_relacionados";
    },
    isInsolutoField(fieldCode) {
      const code = String(fieldCode || "").toLowerCase();
      return code === "saldo_insoluto" || code === "balance";
    },
    getRelatedDocuments(item, fieldCode) {
      const code = String(fieldCode || "").toLowerCase();
      const source = code === "cfdis_relacionados"
        ? item?.cfdis_relacionados
        : item?.documentos_relacionados;
      const raw = String(source || "").trim();
      if (!raw) {
        return [];
      }

      return raw
        .split("|")
        .map((value) => this.formatUuid(value))
        .filter((value) => value !== "");
    },
    formatCell(item, field) {
      let value = item?.[field.code];

      if (field.type === "currency") {
        // Si tienes $formatCurrency global, lo usamos
        const formatted = typeof this.$formatCurrency === "function"
          ? this.$formatCurrency(value)
          : this.formatCurrency(value);

        return String(formatted)
          .replace(/^\$/, "$ ")
          .replace(/^-\$/, "-$ ");
      }

      if (field.type === "date") {
        return typeof this.$formatDate === "function"
          ? this.$formatDate(value)
          : value;
      }

      if (this.$options.UUID_FIELD_CODES.includes(String(field.code || "").toLowerCase())) {
        return String(value ?? "").toUpperCase();
      }

      return value ?? "";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return typeof this.$formatDate === "function"
        ? this.$formatDate(value)
        : value;
    },
    formatUuid(value) {
      return String(value || "").trim().toUpperCase();
    },
    isUuidField(fieldCode) {
      return this.$options.UUID_FIELD_CODES.includes(String(fieldCode || "").toLowerCase());
    },
    openCfdiPreview(uuid, context = {}) {
      const normalized = String(uuid || '').trim().toUpperCase();
      if (!normalized) return;
      this.cfdiPreviewModal.uuid       = normalized;
      this.cfdiPreviewModal.sourceItem = context.sourceItem || null;
      this.cfdiPreviewModal.fieldCode  = context.fieldCode || null;
      this.cfdiPreviewModal.visible    = true;
    },
    closeCfdiPreview() {
      this.cfdiPreviewModal.visible = false;
      this.cfdiPreviewModal.uuid    = '';
      this.cfdiPreviewModal.sourceItem = null;
      this.cfdiPreviewModal.fieldCode = null;
    },
  },
};
</script>
<style scoped>
    .table td, .table th {
        font-size: 11px;
        white-space: nowrap;
    }

  .fx-amount {
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  .insoluto-value {
    font-weight: 700;
  }

  .related-doc-line {
    margin-bottom: 4px;
  }

  .related-doc-line:last-child {
    margin-bottom: 0;
  }

  .related-doc-badge {
    display: inline-flex;
    align-items: center;
    width: 100%;
    min-height: 22px;
    box-sizing: border-box;
    background: var(--theme-deafult);
    color: var(--white);
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 10px;
    line-height: 1.3;
  }

  .related-doc-btn {
    border: 0;
    text-align: left;
  }

  .related-payments-table td,
  .related-payments-table th {
    font-size: 11px;
    white-space: nowrap;
  }

  .actions-cell {
    white-space: nowrap;
    min-width: 170px;
  }

  .action-btn {
    margin-right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1;
  }

  .action-label {
    font-weight: 600;
  }

  .action-btn:last-child {
    margin-right: 0;
  }

  :deep(.popover.fx-popover) {
    border-color: var(--theme-deafult);
  }

  :deep(.popover.fx-popover .popover-header) {
    background: var(--theme-deafult);
    color: var(--white);
    border-bottom: 1px solid var(--theme-deafult);
  }

  :deep(.popover.fx-popover .popover-body) {
    color: var(--theme-font-color);
  }

  .uuid-link-btn {
    background: none;
    border: none;
    padding: 0;
    color: var(--theme-deafult);
    font-size: 10px;
    font-family: monospace;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: pointer;
    white-space: nowrap;
  }

  .uuid-link-btn:hover {
    text-decoration: underline solid;
  }

  .multiline {
    white-space: pre-line !important;
    line-height: 1.35;
  }
</style>